import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  LayoutDashboard, Calendar, FileText, Image as ImageIcon, Users, ShoppingBag,
  Star, Mail, Inbox, Settings as SettingsIcon, Menu, Send, Building2, Download,
  Plus, Trash2, Pencil, Loader2, Package, RefreshCw, CreditCard, Undo2,
} from "lucide-react";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type Addon = Database["public"]["Tables"]["addons"]["Row"];
type AddonOption = Database["public"]["Tables"]["addon_options"]["Row"];
type Studio = Database["public"]["Tables"]["studios"]["Row"];
type Product = Database["public"]["Tables"]["products"]["Row"];

const adminNav = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Bookings", to: "/admin/bookings", icon: Calendar },
  { label: "Customers", to: "/admin/customers", icon: Users },
  { label: "Studios", to: "/admin/studios", icon: Building2 },
  { label: "Add-ons", to: "/admin/addons", icon: Package },
  { label: "Email Log", to: "/admin/emails", icon: Send },
  { label: "Stripe Sync", to: "/admin/stripe/sync", icon: RefreshCw },
  { label: "Stripe Events", to: "/admin/stripe/events", icon: CreditCard },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Gallery", to: "/admin/gallery", icon: ImageIcon },
  { label: "Team", to: "/admin/team", icon: Users },
  { label: "Products", to: "/admin/products", icon: ShoppingBag },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Subscribers", to: "/admin/subscribers", icon: Mail },
  { label: "Contact", to: "/admin/contact", icon: Inbox },
  { label: "Settings", to: "/admin/settings", icon: SettingsIcon },
  { label: "Import from WP", to: "/admin/wp-import", icon: Download },
  { label: "Refund Policies", to: "/admin/refund-policies", icon: Undo2 },
];

const Sidebar = ({ open, toggle }: { open: boolean; toggle: () => void }) => {
  const loc = useLocation();
  return (
    <>
      <button onClick={toggle} className="lg:hidden fixed top-20 left-4 z-40 bg-panel border border-border p-2">
        <Menu className="w-5 h-5" />
      </button>
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-surface border-r border-border z-30 transition-transform overflow-y-auto ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav className="p-4 space-y-1 mt-4">
          {adminNav.map((item) => (
            <Link key={item.to} to={item.to}
              className={`flex items-center gap-3 px-3 py-2 text-sm font-body transition-colors ${loc.pathname === item.to ? "bg-primary/10 text-primary border-l-2 border-primary" : "text-muted-foreground hover:text-foreground hover:bg-panel"}`}>
              <item.icon className="w-4 h-4" />{item.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
};

const AdminAddons = () => {
  const [sbOpen, setSbOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editing, setEditing] = useState<Addon | null>(null);
  const [draft, setDraft] = useState<Partial<Addon>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [options, setOptions] = useState<AddonOption[]>([]);
  const [linkedStudios, setLinkedStudios] = useState<Set<string>>(new Set());
  const [linkedProducts, setLinkedProducts] = useState<Set<string>>(new Set());
  const [newOpt, setNewOpt] = useState({ label: "", price_delta: "" });

  const load = async () => {
    setLoading(true);
    const [{ data: a }, { data: s }, { data: p }] = await Promise.all([
      supabase.from("addons").select("*").is("product_id", null).is("studio_id", null).order("display_order"),
      supabase.from("studios").select("*").order("display_order"),
      supabase.from("products").select("*").order("display_order"),
    ]);
    setAddons(a ?? []);
    setStudios(s ?? []);
    setProducts(p ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openEdit = async (a: Addon | null) => {
    setEditing(a);
    setDraft(a ?? { name: "", price: 0, active: true, display_order: addons.length, pricing_unit: "flat" });
    setOptions([]);
    setLinkedStudios(new Set());
    setLinkedProducts(new Set());
    setNewOpt({ label: "", price_delta: "" });
    setDialogOpen(true);
    if (a) {
      const [{ data: opts }, { data: ls }, { data: lp }] = await Promise.all([
        supabase.from("addon_options").select("*").eq("addon_id", a.id).order("display_order"),
        supabase.from("addon_studios").select("studio_id").eq("addon_id", a.id),
        supabase.from("addon_products").select("product_id").eq("addon_id", a.id),
      ]);
      setOptions(opts ?? []);
      setLinkedStudios(new Set((ls ?? []).map((x: any) => x.studio_id)));
      setLinkedProducts(new Set((lp ?? []).map((x: any) => x.product_id)));
    }
  };

  const save = async () => {
    if (!draft.name?.trim()) { toast.error("Name required"); return; }
    const payload = {
      name: draft.name.trim(),
      price: Number(draft.price ?? 0),
      active: draft.active ?? true,
      display_order: draft.display_order ?? 0,
      pricing_unit: draft.pricing_unit ?? "flat",
      max_quantity: draft.max_quantity ?? null,
      stock: (draft as any).stock ?? null,
      description: draft.description ?? null,
      product_id: null,
      studio_id: null,
    };
    let id = editing?.id;
    if (editing) {
      const { error } = await supabase.from("addons").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
    } else {
      const { data, error } = await supabase.from("addons").insert(payload).select("id").single();
      if (error || !data) return toast.error(error?.message ?? "Failed");
      id = data.id;
    }
    if (!id) return;
    // sync links
    await supabase.from("addon_studios").delete().eq("addon_id", id);
    await supabase.from("addon_products").delete().eq("addon_id", id);
    if (linkedStudios.size) {
      await supabase.from("addon_studios").insert([...linkedStudios].map(sid => ({ addon_id: id!, studio_id: sid })));
    }
    if (linkedProducts.size) {
      await supabase.from("addon_products").insert([...linkedProducts].map(pid => ({ addon_id: id!, product_id: pid })));
    }
    toast.success("Saved");
    setDialogOpen(false);
    setEditing(null);
    setDraft({});
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this add-on and all its options/links?")) return;
    const { error } = await supabase.from("addons").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  const addOption = async () => {
    if (!editing) { toast.error("Save the add-on first"); return; }
    // Allow bulk entry: split on commas or newlines so the admin can paste a
    // list like "pink, royal blue, navy blue, red" in one go.
    const labels = newOpt.label
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (labels.length === 0) return;
    const priceDelta = Number(newOpt.price_delta) || 0;
    const rows = labels.map((label, i) => ({
      addon_id: editing.id,
      label,
      price_delta: priceDelta,
      display_order: options.length + i,
    }));
    const { data, error } = await supabase.from("addon_options").insert(rows).select();
    if (error || !data) return toast.error(error?.message ?? "Failed");
    setOptions([...options, ...data]);
    setNewOpt({ label: "", price_delta: "" });
    toast.success(`Added ${data.length} option${data.length === 1 ? "" : "s"}`);
  };
  const removeOption = async (id: string) => {
    const { error } = await supabase.from("addon_options").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setOptions(options.filter(o => o.id !== id));
  };

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    setter(n);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar open={sbOpen} toggle={() => setSbOpen(!sbOpen)} />
      <main className="lg:ml-64 pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-display text-3xl tracking-widest uppercase">Global Add-ons</h1>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mt-1">Backdrop colors, throne chairs, etc. Assign to any product or studio.</p>
            </div>
            <Button onClick={() => openEdit(null)} className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-1" /> New add-on
            </Button>
          </div>

          {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : (
            <div className="space-y-2">
              {addons.length === 0 && <p className="text-sm text-muted-foreground font-body">No global add-ons yet.</p>}
              {addons.map(a => (
                <div key={a.id} className="bg-panel border border-border p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-body text-sm">{a.name} {!a.active && <span className="ml-2 text-xs text-muted-foreground">(hidden)</span>}</p>
                    <p className="text-xs text-muted-foreground font-mono">${Number(a.price).toFixed(2)} {a.pricing_unit !== "flat" ? `/ ${a.pricing_unit}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(a)}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => remove(a.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setDraft({}); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit add-on" : "New add-on"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={draft.name ?? ""} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Backdrop color" />
              </div>
              <div>
                <Label>Base price ($)</Label>
                <Input type="number" step="0.01" value={draft.price ?? 0} onChange={e => setDraft({ ...draft, price: Number(e.target.value) as any })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max quantity (optional)</Label>
                <Input type="number" min="1" value={draft.max_quantity ?? ""} onChange={e => setDraft({ ...draft, max_quantity: e.target.value ? parseInt(e.target.value, 10) : null as any })} placeholder="—" />
                <p className="text-xs text-muted-foreground mt-1">Max quantity a single booking can pick.</p>
              </div>
              <div>
                <Label>Total stock (optional)</Label>
                <Input type="number" min="0" value={(draft as any).stock ?? ""} onChange={e => setDraft({ ...draft, stock: e.target.value ? parseInt(e.target.value, 10) : null } as any)} placeholder="Unlimited" />
                <p className="text-xs text-muted-foreground mt-1">Total inventory across all bookings.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={draft.active ?? true} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
                  <Label>Active</Label>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="border-t border-border pt-4">
              <Label className="font-mono text-xs uppercase tracking-widest">Options (e.g., color choices)</Label>
              {!editing && <p className="text-xs text-muted-foreground font-body mt-1">Save the add-on first, then add options.</p>}
              {editing && (
                <div className="space-y-2 mt-2">
                  {options.map(o => (
                    <div key={o.id} className="flex items-center gap-2 bg-panel border border-border p-2">
                      <span className="text-sm flex-1">{o.label}</span>
                      <span className="text-xs font-mono text-muted-foreground">{Number(o.price_delta) >= 0 ? "+" : ""}${Number(o.price_delta).toFixed(2)}</span>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => removeOption(o.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      placeholder="pink, royal blue, navy blue, red…"
                      value={newOpt.label}
                      onChange={e => setNewOpt({ ...newOpt, label: e.target.value })}
                    />
                    <Input type="number" step="0.01" placeholder="+price delta" value={newOpt.price_delta} onChange={e => setNewOpt({ ...newOpt, price_delta: e.target.value })} className="w-32" />
                    <Button size="sm" onClick={addOption}><Plus className="w-3 h-3" /></Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground font-body">
                    Tip: paste a comma- or newline-separated list to create many options at once. All share the price delta above.
                  </p>
                </div>
              )}
            </div>

            {/* Studio links */}
            <div className="border-t border-border pt-4">
              <Label className="font-mono text-xs uppercase tracking-widest">Apply to studios</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {studios.map(s => (
                  <label key={s.id} className="flex items-center gap-2 bg-panel border border-border p-2 text-sm cursor-pointer">
                    <Checkbox checked={linkedStudios.has(s.id)} onCheckedChange={() => toggle(linkedStudios, setLinkedStudios, s.id)} />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>

            {/* Product links */}
            <div className="border-t border-border pt-4">
              <Label className="font-mono text-xs uppercase tracking-widest">Apply to products</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-60 overflow-y-auto">
                {products.map(p => (
                  <label key={p.id} className="flex items-center gap-2 bg-panel border border-border p-2 text-sm cursor-pointer">
                    <Checkbox checked={linkedProducts.has(p.id)} onCheckedChange={() => toggle(linkedProducts, setLinkedProducts, p.id)} />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); setDraft({}); }}>Cancel</Button>
            <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAddons;